import DOMPurify from 'dompurify';
import { ohneDoppeltenGruss } from '../../supabase/functions/_shared/grussformel';
import { normalisiereMailLink } from './mailLink';

// cid:-Bilder können erst nach dem Sanitizing asynchron aus Graph aufgelöst werden.
// Vorher wird src="cid:X" in ein data-cid-Attribut umgeschrieben — DOMPurify würde
// das unbekannte cid:-Schema sonst verwerfen. data-*-Attribute überleben das Sanitizing.
export const CID_ATTR = 'data-cid';

export function extrahiereCidBilder(html: string): { html: string; cids: string[] } {
  const cids: string[] = [];
  const ersetzt = html.replace(
    /(<img\b[^>]*?\s)src\s*=\s*(["'])cid:([^"']+)\2/gi,
    (_ganz, prefix: string, _quote: string, cid: string) => {
      if (!cids.includes(cid)) cids.push(cid);
      return `${prefix}${CID_ATTR}="${cid}" src=""`;
    },
  );
  return { html: ersetzt, cids };
}

// Läuft nur im Browser (DOMPurify braucht ein DOM) — deshalb nicht unit-getestet.
export function sanitizeEmailHtml(html: string): { html: string; cids: string[] } {
  const { html: vorbereitet, cids } = extrahiereCidBilder(html);
  // #554: Links öffnen in einem neuen Tab (die Mail steht direkt in der App-Seite — ein Klick
  // verließ sonst die App) und bekommen bei „www.…“ ohne Schema ein https://.
  const linkHaken = (node: Element) => {
    if (node.tagName !== 'A' || !node.hasAttribute('href')) return;
    const ziel = normalisiereMailLink(node.getAttribute('href'));
    if (!ziel) { node.removeAttribute('href'); return; }
    node.setAttribute('href', ziel);
    if (!ziel.startsWith('#')) { node.setAttribute('target', '_blank'); node.setAttribute('rel', 'noopener noreferrer'); }
  };
  DOMPurify.addHook('afterSanitizeAttributes', linkHaken);
  try {
    const sauber = DOMPurify.sanitize(vorbereitet, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
      ADD_ATTR: ['target'],
    });
    return { html: sauber, cids };
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
}

// ── Zitat der Ursprungsmail als HTML (Antworten/Weiterleiten) ─────────────────────
// Freigabe User 15.10.2026 (Variante 2): Das Zitat behält Absätze, Fett/Kursiv, Listen, Tabellen und
// Links, statt zu reinem Text geplättet zu werden. Fremde Styles, <head>, Skripte und eingebettete
// cid:/data:-Bilder fliegen raus – sie würden das eigene Briefpapier verschieben bzw. in der Antwort
// ohnehin nicht funktionieren. Nur https-Bilder bleiben.

/** Erlaubte Tags im Zitat (alles andere wird entfernt, der Text darin bleibt erhalten). */
export const ZITAT_TAGS = [
  'p', 'br', 'div', 'span', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'sub', 'sup', 'small', 'big',
  'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'hr', 'center', 'font',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'img',
];

/** Erlaubte Attribute im Zitat – bewusst ohne style/class/id (fremdes Layout). */
export const ZITAT_ATTRIBUTE = ['href', 'src', 'alt', 'title', 'width', 'height', 'colspan', 'rowspan', 'align', 'valign'];

/**
 * Reine Text-Vorbereitung (testbar ohne DOM): nur den <body>-Inhalt nehmen, Kommentare, <head>, <style>,
 * <script>, <title>, Office-<xml>, Meta/Link entfernen und Bilder ohne https-Quelle streichen.
 */
export function bereiteZitatHtmlVor(html: string): string {
  let h = html || '';
  const body = h.match(/<body\b[^>]*>([\s\S]*?)<\/body\s*>/i);
  if (body) h = body[1];
  return h
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(head|style|script|title|xml|noscript)\b[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<(meta|link|base)\b[^>]*>/gi, '')
    .replace(/<img\b(?![^>]*\bsrc\s*=\s*["']?https:)[^>]*>/gi, '')
    .trim();
}

const ZITAT_TABELLEN_TAGS = new Set(['TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TD', 'TH']);

/** Zitat-HTML für den Editor bzw. die gesendete Antwort (nur im Browser – DOMPurify braucht ein DOM). */
export function bereinigeZitatHtml(html: string): string {
  // Newsletter bringen feste Breiten mit (teils mehrere tausend Pixel) – das Zitat liefe sonst rechts
  // aus dem Briefbogen. Tabellen verlieren ihre Breiten, Bilder bleiben höchstens zitatbreit.
  // Die style-Angaben setzen wir selbst (nach dem Bereinigen); fremde style-Attribute bleiben verboten.
  const begrenze = (node: Element) => {
    if (ZITAT_TABELLEN_TAGS.has(node.tagName)) {
      node.removeAttribute('width');
      node.removeAttribute('height');
      if (node.tagName === 'TABLE') node.setAttribute('style', 'max-width:100%;');
    } else if (node.tagName === 'IMG') {
      node.setAttribute('style', 'max-width:100%;height:auto;');
    }
  };
  DOMPurify.addHook('afterSanitizeAttributes', begrenze);
  try {
    return DOMPurify.sanitize(bereiteZitatHtmlVor(html), {
      ALLOWED_TAGS: ZITAT_TAGS,
      ALLOWED_ATTR: ZITAT_ATTRIBUTE,
      ALLOW_DATA_ATTR: false,
      ALLOWED_URI_REGEXP: /^(?:https:|http:|mailto:|tel:)/i,
    });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
}

/**
 * Read-only-Vorschau einer Vorlage im Briefpapier: Inhalt in den {{inhalt}}-Slot,
 * Signatur in den {{signatur}}-Slot; fehlen die Slots, wird vor </body> angehängt.
 * Gegenstück zum editierbaren buildBriefDoc in EmailVerfassen (dort mit contenteditable-
 * Zone) — hier bewusst ohne Editier-Attribute für die Vorlagen-Live-Vorschau.
 */
export function baueBriefVorschau(bpHtml: string, inhaltHtml: string, signaturHtml: string): string {
  const inhalt = `<div>${inhaltHtml || ''}</div>`;
  const sig = `<div>${signaturHtml || ''}</div>`;
  let doc = ohneDoppeltenGruss(bpHtml && bpHtml.trim() ? bpHtml : '<html><body></body></html>', signaturHtml);
  doc = doc.includes('{{inhalt}}') ? doc.split('{{inhalt}}').join(inhalt) : doc.replace('</body>', `${inhalt}</body>`);
  doc = doc.includes('{{signatur}}') ? doc.split('{{signatur}}').join(sig) : doc.replace('</body>', `${sig}</body>`);
  return doc;
}

/**
 * Dekodiert HTML-Entities in Klartext (&nbsp;, &amp;, numerische &#…;) und entfernt
 * unsichtbare Zeichen (BOM/Zero-Width). Für Texte, die aus E-Mail-HTML stammen und als
 * Plain-Text angezeigt werden (z. B. Ticket-Beschreibungen aus dem KI-Posteingang) —
 * sonst stehen dort rohe "&nbsp;"/"&#65279;" im UI. Reihenfolge: numerisch → benannte →
 * &amp; als LETZTES (damit "&amp;nbsp;" korrekt zu "&nbsp;"-Literal wird, nicht zu Leerzeichen).
 */
export function dekodiereHtmlEntities(text: string): string {
  return (text || '')
    .replace(/&#(\d+);/g, (_, d) => {
      const cp = parseInt(d, 10);
      return Number.isFinite(cp) && cp >= 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : '';
    })
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => {
      const cp = parseInt(h, 16);
      return Number.isFinite(cp) && cp >= 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : '';
    })
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&(#39|apos);/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/[​‌‍﻿]/g, '');
}
